import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import AuditorFieldOptionsController from './auditorFieldOptions.controller';
import { Layout, PageHeader, SectionCard, EmptyState, StatusTag, CardSkeleton } from '../common';
import { Input, Button, Space, Tooltip } from 'antd';
import { Plus, Trash2, Save, ListTree, ChevronRight, ChevronDown, Tags, FolderTree, MousePointerClick } from 'lucide-react';

class ShelfAuditorFieldOptions extends Inherit(AuditorFieldOptionsController, Component) {
    render() {
        const { loading, saving, categories, categoryInput, selectedCategory, segmentInput } = this.state;
        const activeCategory = this.activeCategory;

        return (
            <Layout current="auditor-field-options" user={this.user}>
                {this.props.contextHolder}

                <PageHeader
                    icon={<ListTree />}
                    title="Opciones de auditoría"
                    description="Categorías y segmentos que el auditor elige al registrar un producto. Selecciona una categoría para gestionar sus segmentos."
                    actions={
                        <Button type="primary" icon={<Save />} loading={saving} disabled={loading} onClick={() => this.saveOptions()}>
                            Guardar cambios
                        </Button>
                    }
                />

                {loading ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" aria-busy="true">
                        <span className="sr-only">Cargando…</span>
                        <CardSkeleton rows={4} />
                        <CardSkeleton rows={4} />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                        {/* ── Categories column ── */}
                        <SectionCard
                            title="Categorías"
                            description={`${categories.length} categorías`}
                            icon={<FolderTree />}
                            flush>
                            <div className="p-4 border-b border-surface-100">
                                <Space.Compact className="w-full">
                                    <Input
                                        placeholder="Nueva categoría"
                                        value={categoryInput}
                                        onChange={(e) => this.setCategoryInput(e.target.value)}
                                        onPressEnter={() => this.addCategory()}
                                    />
                                    <Button icon={<Plus />} onClick={() => this.addCategory()}>
                                        Añadir
                                    </Button>
                                </Space.Compact>
                            </div>

                            {categories.length ? (
                                <ul className="m-0 p-2 list-none space-y-0.5">
                                    {categories.map((cat) => {
                                        const isSelected = selectedCategory === cat.name;
                                        return (
                                            <li
                                                key={cat.name}
                                                className={`group flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                                                    isSelected ? 'border-primary-200 bg-primary-50' : 'border-transparent hover:bg-surface-50'
                                                }`}
                                                onClick={() => this.selectCategory(cat.name)}>
                                                {isSelected ? (
                                                    <ChevronDown className="shrink-0 text-primary-600 text-[13px]" />
                                                ) : (
                                                    <ChevronRight className="shrink-0 text-surface-400 text-[13px]" />
                                                )}
                                                <span className={`flex-1 truncate text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-surface-900'}`}>
                                                    {cat.name}
                                                </span>
                                                <StatusTag tone={isSelected ? 'brand' : 'neutral'} className="tabular-nums">
                                                    {cat.segments.length}
                                                </StatusTag>
                                                <Tooltip title="Eliminar categoría">
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        danger
                                                        icon={<Trash2 />}
                                                        aria-label="Eliminar categoría"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            this.removeCategory(cat.name);
                                                        }}
                                                    />
                                                </Tooltip>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <EmptyState icon={<FolderTree />} title="Sin categorías" description="Añade la primera categoría arriba" />
                            )}
                        </SectionCard>

                        {/* ── Segments column ── */}
                        <SectionCard
                            title={
                                activeCategory ? (
                                    <span>
                                        Segmentos de <span className="text-primary-600">{activeCategory.name}</span>
                                    </span>
                                ) : (
                                    'Segmentos'
                                )
                            }
                            description={activeCategory ? `${activeCategory.segments.length} segmentos` : undefined}
                            icon={<Tags />}
                            iconTone={activeCategory ? 'brand' : 'neutral'}
                            flush>
                            {!activeCategory ? (
                                <EmptyState
                                    icon={<MousePointerClick />}
                                    title="Ninguna categoría seleccionada"
                                    description="Selecciona una categoría para ver y editar sus segmentos"
                                />
                            ) : (
                                <>
                                    <div className="p-4 border-b border-surface-100">
                                        <Space.Compact className="w-full">
                                            <Input
                                                placeholder={`Nuevo segmento en ${activeCategory.name}`}
                                                value={segmentInput}
                                                onChange={(e) => this.setSegmentInput(e.target.value)}
                                                onPressEnter={() => this.addSegment()}
                                            />
                                            <Button icon={<Plus />} onClick={() => this.addSegment()}>
                                                Añadir
                                            </Button>
                                        </Space.Compact>
                                    </div>
                                    {activeCategory.segments.length ? (
                                        <ul className="m-0 p-0 list-none divide-y divide-surface-100">
                                            {activeCategory.segments.map((seg) => (
                                                <li key={seg} className="flex items-center justify-between gap-3 px-5 py-2.5">
                                                    <StatusTag tone="violet">{seg}</StatusTag>
                                                    <Tooltip title="Eliminar segmento">
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            danger
                                                            icon={<Trash2 />}
                                                            aria-label="Eliminar segmento"
                                                            onClick={() => this.removeSegment(activeCategory.name, seg)}
                                                        />
                                                    </Tooltip>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <EmptyState icon={<Tags />} title="Sin segmentos en esta categoría" />
                                    )}
                                </>
                            )}
                        </SectionCard>
                    </div>
                )}
            </Layout>
        );
    }
}

export default withNavigate(ShelfAuditorFieldOptions);
